/**
 * @fileoverview The share in progress, kept on the device while the sheet is
 * open, so a share survives the phone (owner, 2026-10-12: "you go to
 * Instagram, you share, and you close that out, you come back to the plant
 * and Grow, the plant reloads and you lose all of the sharer settings").
 *
 * On Android, Chrome can throw Grow's tab away while the gardener is in
 * Instagram and load it afresh on the way back. Everything the sheet holds
 * (the photographs picked and their order, the words, each crop, the screen
 * it was on) is written here as it changes, and the card that shares the
 * same thing opens the sheet again on load (useResumeShare) on the same
 * screen with the same choices. Closing the sheet any way (Cancel, Escape, a
 * tap outside it) forgets it, and so does leaving the page in the app; an old
 * one is dropped after SHARE_DRAFT_KEEP_MS, and one in a shape this build
 * cannot put back is left alone. The look (size, layout, what is printed) is
 * kept apart, for every share (shareStyle).
 *
 * The screens are six (owner, 2026-10-15): Pick, Layout, Size, Details,
 * Crop, Share (SHARE_STEPS). A draft from the build before, on its one
 * Arrange screen, is put back on Layout; its open tool is not kept.
 *
 * Where: SharePlantDialog (writes, reads, forgets); PlantCard, LocationCard
 * and the season review (useResumeShare).
 * API: none; localStorage under SHARE_DRAFT_STORAGE_KEY.
 *
 * @module shareDraft
 */

import React from 'react';
import { SHARE_DRAFT_KEEP_MS, SHARE_DRAFT_STORAGE_KEY } from '@/config/tunables/plantList';
import type { ShareElements } from './shareFrames';

/** The share studio's screens, in order: pick the photographs, the layout, the size, what is printed, each crop, then share. */
export const SHARE_STEPS = ['pick', 'layout', 'size', 'details', 'crop', 'share'] as const;
/** One of those screens. */
export type ShareStep = typeof SHARE_STEPS[number];

/** The share in progress, as the sheet holds it. */
export interface ShareDraft {
  /** What is being shared: "plant:12", "location:4", "season:2026" (shareDraftKey). */
  subject: string;
  /** The photographs going out, in order, and the one in view. */
  selection: { picked: number[]; shown: number | null };
  /** One line on every picture, or a line each. */
  sameWords: boolean;
  /** The one line. */
  sharedWords: string;
  /** Each photograph's own line, by its id. */
  perWords: Record<number, string>;
  /**
   * Where each photograph sits in its frame, by its id (a draft from the build before keeps them
   * per size, "12:square"; useShareStory reads those back as the photograph's). a is the shape
   * of the slot it was cropped in (B86).
   */
  focus: Record<string, { x: number; y: number; z?: number; a?: number }>;
  /** The screen it was on. */
  step: ShareStep;
  /** A location's share: what its own pictures print. */
  coverElements?: ShareElements;
  /** A location's share: what its plants' photos print. */
  plantElements?: ShareElements;
  /** When it was last written, ms. */
  at: number;
}

/**
 * The key a share is kept under: its kind and id, or a season's year.
 *
 * @param kind - 'plant', 'location' or 'season'.
 * @param id - The plant's or location's id, or the season's year.
 * @returns "plant:12".
 */
export const shareDraftKey = (kind: 'plant' | 'location' | 'season', id: number): string => `${kind}:${id}`;

/** The screens a draft may name; any other (a build that named them differently) is not put back. */
const DRAFT_STEPS: ReadonlySet<unknown> = new Set(SHARE_STEPS);

/**
 * The share in progress for this subject, if one is kept, not too old, and
 * in a shape the sheet can put back. A draft naming a screen this build
 * does not have (written by another build under the same key) is left
 * alone: put back, it opened a sheet with no screen on it. The build
 * before's Arrange is put back as Layout.
 *
 * @param subject - shareDraftKey's key.
 * @returns The draft, or null.
 */
export function readShareDraft(subject: string): ShareDraft | null {
  try {
    const stored = window.localStorage.getItem(SHARE_DRAFT_STORAGE_KEY);
    if (!stored) return null;
    const draft = JSON.parse(stored) as Omit<ShareDraft, 'step'> & { step: unknown };
    if (!draft || draft.subject !== subject || !Array.isArray(draft.selection?.picked)) return null;
    // The build before had one Arrange screen: its draft opens on Layout.
    if (draft.step === 'arrange') draft.step = 'layout';
    if (!DRAFT_STEPS.has(draft.step)) return null;
    if (!(Date.now() - Number(draft.at) < SHARE_DRAFT_KEEP_MS)) return null;
    return draft as ShareDraft;
  } catch {
    return null;
  }
}

/**
 * Keeps the share in progress (one at a time: a new share replaces it).
 *
 * @param draft - The sheet's state, without its time.
 */
export function writeShareDraft(draft: Omit<ShareDraft, 'at'>): void {
  try {
    window.localStorage.setItem(SHARE_DRAFT_STORAGE_KEY, JSON.stringify({ ...draft, at: Date.now() }));
  } catch {
    /* private mode or full: the share just will not survive a reload */
  }
}

/**
 * Forgets the share in progress for this subject (the sheet was closed).
 *
 * @param subject - shareDraftKey's key.
 */
export function clearShareDraft(subject: string): void {
  try {
    const stored = window.localStorage.getItem(SHARE_DRAFT_STORAGE_KEY);
    if (stored && (JSON.parse(stored) as ShareDraft)?.subject === subject) window.localStorage.removeItem(SHARE_DRAFT_STORAGE_KEY);
  } catch {
    /* nothing to forget */
  }
}

/**
 * Opens a card's share sheet again on load when a share of the same thing
 * was in progress (the tab was reloaded while the gardener was in another
 * app). Once per mount.
 *
 * @param subject - shareDraftKey's key, or null while it is not known yet.
 * @param open - Opens the sheet.
 */
export function useResumeShare(subject: string | null, open: () => void): void {
  const done = React.useRef(false);
  React.useEffect(() => {
    if (done.current || !subject) return;
    done.current = true;
    if (readShareDraft(subject)) open();
    // The opener is the card's own setter; read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject]);
}
